import { TEMPLATES } from '@/features/campaigns/types';

export const TEMPLATE_OPTIONS = [
  { value: '0', label: TEMPLATES[0] ?? 'Simple IVR' },
  { value: '1', label: TEMPLATES[1] ?? 'DTMF' },
  { value: '2', label: TEMPLATES[2] ?? 'Call Patch' },
  { value: '3', label: TEMPLATES[3] ?? 'Custom IVR' },
  { value: '7', label: TEMPLATES[7] ?? 'TTS Simple IVR' },
  { value: '8', label: TEMPLATES[8] ?? 'TTS DTMF' },
  { value: '9', label: TEMPLATES[9] ?? 'TTS Call Patch' },
  { value: '10', label: 'TTS IVR' },
] as const;

export type TemplateId = (typeof TEMPLATE_OPTIONS)[number]['value'];

export const TTS_TEMPLATES = new Set<TemplateId>(['7', '8', '9']);
export const NEEDS_PROMPTS = new Set<TemplateId>(['0', '1', '2', '3']);
export const HAS_DTMF = new Set<TemplateId>(['1', '8']);
export const HAS_CALL_PATCH = new Set<TemplateId>(['2', '9']);
export const HAS_CUSTOM_IVR = new Set<TemplateId>(['3']);
export const IS_CONTINUOUS = (t: TemplateId) => t === '10';
const CONTINUOUS_INNER = new Set<string>(['7', '8', '9']);


export const WEEK_DAYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;

export type WeekDay = (typeof WEEK_DAYS)[number];

export interface DaySchedule {
  isEnable: boolean;
  /** HH:mm:ss — always padded on parse/save. */
  startTime: string;
  endTime: string;
}
export type WeekSchedule = Record<WeekDay, DaySchedule>;

/** Business window — a day may dial between 09:00:00 and 21:00:00 only. */
export const SCHEDULE_MIN_START = '09:00:00';
export const SCHEDULE_MAX_END = '21:00:00';

/** Normalize HH:mm | HH:mm:ss → HH:mm:ss ('' stays ''). */
export function padTime(t: string): string {
  if (!t) return '';
  return t.length === 5 ? `${t}:00` : t;
}

function defaultDaySchedule(): DaySchedule {
  return { isEnable: false, startTime: '', endTime: '' };
}
export function defaultWeekSchedule(): WeekSchedule {
  return Object.fromEntries(WEEK_DAYS.map((d) => [d, defaultDaySchedule()])) as WeekSchedule;
}

/** Safe parse of the stored JSON string — bad/empty input falls back to defaults. */
export function parseContinuousSchedule(json: string): WeekSchedule {
  const base = defaultWeekSchedule();
  if (!json || json.trim() === '') return base;
  try {
    const parsed = JSON.parse(json) as Record<string, Partial<DaySchedule>>;
    for (const day of WEEK_DAYS) {
      const p = parsed[day];
      if (p && typeof p === 'object') {
        base[day] = {
          isEnable: Boolean(p.isEnable),
          startTime: padTime(typeof p.startTime === 'string' ? p.startTime : ''),
          endTime: padTime(typeof p.endTime === 'string' ? p.endTime : ''),
        };
      }
    }
    return base;
  } catch {
    return base;
  }
}

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/;

/**
 * Full continuous-schedule validation: dates + weekly windows.
 * Used by the wizard's validateStep AND handleSubmit (stepper jumps skip
 * per-step validation, so the final submit re-checks).
 */
export function validateContinuousSchedule(v: {
  startDate: string;
  endDate: string;
  continuousSchedule: string;
}): string | null {
  if (!v.startDate) return 'Pick a start date.';
  if (!v.endDate) return 'Pick an end date.';

  const startOnly = new Date(v.startDate);
  const startDay = new Date(startOnly.getFullYear(), startOnly.getMonth(), startOnly.getDate());
  const endOnly = new Date(v.endDate);
  const endDay = new Date(endOnly.getFullYear(), endOnly.getMonth(), endOnly.getDate());
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (startDay < today) return 'Start date cannot be in the past.';
  if (endDay < startDay) return 'End date cannot be before the start date.';

  const schedule = parseContinuousSchedule(v.continuousSchedule);
  let anyEnabled = false;
  for (const day of WEEK_DAYS) {
    const d = schedule[day];
    if (!d.isEnable) continue;
    anyEnabled = true;
    if (!d.startTime || !TIME_RE.test(d.startTime)) return `Pick a valid start time for ${day}.`;
    if (!d.endTime || !TIME_RE.test(d.endTime)) return `Pick a valid end time for ${day}.`;

    // Pad before comparing so 5-char and 6-char forms compare correctly.
    const start = padTime(d.startTime);
    const end = padTime(d.endTime);

    // Business window: 09:00–21:00.
    if (start < SCHEDULE_MIN_START)
      return `${day}: dialing cannot start before 09:00 AM.`;
    if (end > SCHEDULE_MAX_END)
      return `${day}: dialing cannot end after 09:00 PM.`;
    if (end <= start) return `${day}: end time must be after the start time.`;
  }
  if (!anyEnabled) return 'Enable at least one day in the weekly schedule.';
  return null;
}
/**
 * Per-template feature switches. Mirrors the legacy conditional UI:
 *   - voice files: which files (welcome/noinput/wronginput/thanks/noAgent)
 *     can be attached, and whether they're required vs optional.
 *   - menu: whether a menu prompt + wait/re-prompt fields show up.
 *   - dtmf picker: whether the multi-select DTMF input is rendered.
 *   - tts: whether the TTS rows + language/gender block applies.
 *   - SMS sections: which call-outcome SMS toggles render.
 */
export interface TemplateFeatures {
  needsMenu: boolean;
  needsMenuWait: boolean; // shows the menuWaitTime + rePrompt inputs
  hasDtmfPicker: boolean;
  hasPatchList: boolean;
  hasIvr: boolean;
  hasTts: boolean;
  hasTtsFlow: boolean;
  /** True only for continuous (10) — schedule card + validation. */
  needsSchedule: boolean;
  /** Required voice files (no toggle). */
  requiredVoiceFiles: Array<'welcome' | 'menu' | 'noinput' | 'wronginput' | 'thanks' | 'noAgent'>;
  /** Optional voice files exposed via a toggle. */
  optionalVoiceFiles: Array<'welcome' | 'noinput' | 'wronginput' | 'thanks'>;
  /** SMS sections shown in Step 5. */
  smsSections: Array<'success' | 'fail' | 'dtmf' | 'patchSuccess' | 'patchFail'>;
}

const NONE: TemplateFeatures = {
  needsMenu: false,
  needsMenuWait: false,
  hasDtmfPicker: false,
  hasPatchList: false,
  hasIvr: false,
  hasTts: false,
  hasTtsFlow: false,
  needsSchedule: false,
  requiredVoiceFiles: [],
  optionalVoiceFiles: [],
  smsSections: [],
};

export const TEMPLATE_FEATURES: Record<TemplateId, TemplateFeatures> = {
  '0': {
    ...NONE,
    requiredVoiceFiles: ['welcome'],
    smsSections: ['success', 'fail'],
  },
  '1': {
    ...NONE,
    needsMenu: true,
    needsMenuWait: true,
    hasDtmfPicker: true,
    requiredVoiceFiles: ['menu'],
    optionalVoiceFiles: ['welcome', 'noinput', 'wronginput', 'thanks'],
    smsSections: ['success', 'fail', 'dtmf'],
  },
  '2': {
    ...NONE,
    needsMenu: true,
    needsMenuWait: true,
    hasPatchList: true,
    requiredVoiceFiles: ['menu', 'noAgent'],
    optionalVoiceFiles: ['welcome', 'noinput', 'wronginput', 'thanks'],
    smsSections: ['success', 'fail', 'dtmf', 'patchSuccess', 'patchFail'],
  },
  '3': {
    ...NONE,
    hasIvr: true,
  },
  '7': {
    ...NONE,
    hasTts: true,
    smsSections: ['success', 'fail'],
  },
  '8': {
    ...NONE,
    needsMenu: true,
    needsMenuWait: true,
    hasDtmfPicker: true,
    hasTts: true,
    requiredVoiceFiles: ['menu'],
    optionalVoiceFiles: ['noinput', 'wronginput', 'thanks'],
    smsSections: ['success', 'fail', 'dtmf'],
  },
  '9': {
    ...NONE,
    needsMenu: true,
    needsMenuWait: true,
    hasPatchList: true,
    hasTts: true,
    requiredVoiceFiles: ['menu', 'noAgent'],
    optionalVoiceFiles: ['noinput', 'wronginput', 'thanks'],
    smsSections: ['success', 'fail', 'patchSuccess', 'patchFail'],
  },
  '10': {
    ...NONE,
    needsSchedule: true,
    hasTtsFlow: true,
  },
};

/**
 * Second arg = the TTS flow's own templateId (stored in form values as
 * `templateIdByTTS`). For template 10, features = inner 7/8/9 features
 * + flow picker + schedule. For everything else, static lookup.
 */
export function templateFeatures(t: TemplateId, innerTtsTemplateId?: string): TemplateFeatures {
  if (t === '10') {
    const base =
      innerTtsTemplateId && CONTINUOUS_INNER.has(innerTtsTemplateId)
        ? TEMPLATE_FEATURES[innerTtsTemplateId as TemplateId]
        : NONE;
    return { ...base, hasTtsFlow: true, needsSchedule: true };
  }
  return TEMPLATE_FEATURES[t] ?? NONE;
}

export const getMinTime = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() - 15);

  return now.toTimeString().slice(0, 5); // HH:mm
};

export const flavorOptions = [
  { value: '7' as TemplateId, label: 'Simple TTS', desc: 'Play a TTS prompt only.' },
  { value: '8' as TemplateId, label: 'TTS DTMF', desc: 'TTS menu + DTMF input.' },
  { value: '9' as TemplateId, label: 'TTS Call Patch', desc: 'TTS menu + agent patch.' },
];
/** Menu wait time + re-prompt validation ranges. */
export const MENU_WAIT_MIN = 1;
export const MENU_WAIT_MAX = 10;
export const RE_PROMPT_MIN = 0;
export const RE_PROMPT_MAX = 3;
/** Retry interval (minutes) — clamped on blur. */
export const RETRY_INTERVAL_MIN = 1;
export const RETRY_INTERVAL_MAX = 200;

/* ---------------- Lookups ---------------- */

export interface BaseUploadRow {
  baseId: number;
  baseName: string;
  uploadedDate?: string;
  totalNumbers?: number;
}

export interface PromptUploadRow {
  promptId: number;
  promptName?: string;
  /** Friendly file name — legacy renders this rather than promptName. */
  fileName?: string;
  /** "welcome" | "menu" | "noinput" | "wronginput" | "thanks" | "noAgent". */
  promptCategory?: string;
  /** 1 = ready, anything else = pending/disabled. */
  promptStatus?: number | string;
  /** Owner of the prompt (returned by /prompts/list). */
  userId?: number;
}

export interface TtsTypeRow {
  /** "text" | "number" | "date" | "time" | "currency", from /api/obd/tts/type/list. */
  tts_type: string;
}

export interface UserLocationRow {
  locationId: number;
  locationName: string;
}

export interface UserCliRow {
  cliId: number;
  cli: string;
  pilotNumber?: string | number;
}

export interface DedicatedCliRow {
  cliId: number;
  cli: string;
  allocated?: number;
}

/**
 * Wire shape returned by `GET /api/obd/custom/ivr/list/{userId}`. The
 * backend ships `id` + `flowName` (legacy naming) — not `ivrId` / `ivrName`
 * as I'd assumed. Keep these field names exactly to match the response.
 */
export interface IvrFlowRow {
  id: number;
  flowName: string;
}

/**
 * Wire shape returned by `GET /api/obd/tts/flow/list/{userId}`. Backend
 * ships `id` + `ttsFlowName` (legacy table column names). Not
 * `ttsFlowId` / `flowName` as the earlier (speculative) type expected.
 */
export interface TtsFlowRow {
  id: number;
  ttsFlowName: string;
  status: number;
  templateId: number;
}

export interface UserLanguageRow {
  language: string;
  languageCode?: string;
  gender?: string;
}

export interface UserGroupAgentRow {
  groupId: number;
  groupName: string;
}

export interface UserModuleRow {
  moduleName: string;
  moduleId?: number;
}

/* ---------------- Compose form values ---------------- */

export interface PatchListRow {
  /** The agent group bound to this DTMF digit. */
  groupId: string;
  groupName: string;
  /** Single DTMF digit the user presses to reach this group. */
  agentDtmf: string;
}

/**
 * Single entry in the legacy ttsList. `ttsType: 'prompt'` rows bind a
 * pre-recorded prompt id; `ttsType: 'variable'` rows bind a numbered
 * variable (1..N) plus a typed format (`text` / `number` / `date` /
 * `time` / `currency`).
 */
export interface TtsRow {
  ttsType: 'prompt' | 'variable';
  /** Prompt id (for `prompt`) or 1-based variable index (for `variable`). */
  value: string;
  /** Empty for `prompt`; one of the TTS types for `variable`. */
  variableType: string;
}

export type BaseFileMode = 'contactList' | 'newBaseFile' | 'oldBaseFile';

export interface VoiceCampaignFormValues {
  /* Step 1 - Basics */
  campaignName: string;
  templateId: TemplateId;
  ttsId: string;
  sendLater: boolean;
  scheduleDate: string; // ISO; empty string means "send now"
  retries: number;
  retryInterval: number; // minutes; server multiplies by 60
  ringDuration: number;

  /* Step 2 - Contacts */
  baseFileMode: BaseFileMode;
  contactList: string;
  baseName: string;
  oldBaseId: string;

  /* Step 3 - Content */
  /** Optional voice file toggles (match the legacy welcomePrompt etc. flags). */
  useWelcomeFile: boolean;
  useNoinputFile: boolean;
  useWronginputFile: boolean;
  useThanksFile: boolean;
  welcomeId: string;
  menuId: string;
  noinputId: string;
  wronginputId: string;
  thanksId: string;
  rePrompt: string;
  repeatMenu: string;
  menuWaitTime: string;
  dtmf: string[]; // selected DTMF options for templates 1/8
  patchList: PatchListRow[];
  noAgentId: string;
  //for continous only
  useIvr: boolean;
  ivrId: string;
  selectedTTSFlowId: string;
  templateIdByTTS: string;

  /* Step 4 - Routing */
  locationIds: string[];
  selectedCLI: string[];

  /* Step 5 - Notifications */
  sendSmsSuccess: boolean;
  smsApiSuccess: string;
  callDurationSMS: number;
  sendSmsFail: boolean;
  smsApiFail: string;
  sendSmsDtmf: boolean;
  smsApiDtmf: string;
  sendSmsPatchSuccess: boolean;
  callPatchSuccessMessage: string;
  sendSmsPatchFail: boolean;
  callPatchFailMessage: string;
  sendWebhook: boolean;
  webhookId: string;

  /* Continuous-campaign schedule (template 10 only; empty for all others) */
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
  continuousSchedule: string;
  continueWithBase: boolean;
}

export const DEFAULT_FORM_VALUES: VoiceCampaignFormValues = {
  campaignName: '',
  templateId: '0',
  templateIdByTTS: '',
  sendLater: false,
  scheduleDate: '',
  retries: 0,
  retryInterval: 0,
  ringDuration: 10,
  baseFileMode: 'contactList',
  contactList: '',
  baseName: '',
  oldBaseId: '',
  useWelcomeFile: false,
  useNoinputFile: false,
  useWronginputFile: false,
  useThanksFile: false,
  welcomeId: '',
  menuId: '',
  noinputId: '',
  wronginputId: '',
  thanksId: '',
  rePrompt: '',
  repeatMenu: '',
  menuWaitTime: '',
  dtmf: [],
  patchList: [],
  noAgentId: '',
  ivrId: '',
  selectedTTSFlowId: '',
  locationIds: [],
  selectedCLI: [],
  sendSmsSuccess: false,
  smsApiSuccess: '',
  callDurationSMS: 0,
  sendSmsFail: false,
  smsApiFail: '',
  sendSmsDtmf: false,
  smsApiDtmf: '',
  sendSmsPatchSuccess: false,
  callPatchSuccessMessage: '',
  sendSmsPatchFail: false,
  callPatchFailMessage: '',
  sendWebhook: false,
  webhookId: '',
  ttsId: '',
  startDate: '',
  endDate: '',
  continuousSchedule: '',
  continueWithBase: false,
  useIvr: false,
};

/* ---------------- Wire format ---------------- */

export interface ComposeCampaignRequest {
  userId: number;
  campaignName: string;
  templateId: string;
  scheduleTime: string;
  sendLater: boolean;
  baseId: number;
  dtmf: string;
  welcomePId: string;
  menuPId: string;
  noInputPId: string;
  wrongInputPId: string;
  thanksPId: string;
  smsSuccessApi: string;
  smsFailApi: string;
  smsDtmfApi: string;
  callDurationSMS: number;
  retries: number;
  retryInterval: number; // seconds (already multiplied)
  ringDuration?: number;
  agentRows: string;
  menuWaitTime: string;
  rePrompt: string;
  ivrId: string;
  location: string;
  clis: string;
  webhook: boolean;
  webhookId: string;
  noAgentId: string;
  callPatchSuccessMessage: string;
  callPatchFailMessage: string;
  ttsFlowId: string;
  repeatMenu: string;
  ttsId: string;
  startDate?: string;
  endDate?: string;
    /** Weekly schedule JSON — template 10 only, '' otherwise. */
  continuousSchedule?: string;
  continueWithBase?: boolean;
  templateIdByTTS: string;
}

export type UpdateCampaignRequest = ComposeCampaignRequest & {
  campaignId: number;
};
